import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import { useListingActions } from '../../../hooks/useListingAdmin';
import { ListingLocation } from '../../../types';
import { DEFAULT_AREA_ID } from '../../../config/areas';
import { hasCoords } from '../../../utils/geo';
import { LISTING_LIMITS, locationForSave } from '../../../utils/listingEdit';
import LazyPinPickerMap from '../../directory/map/LazyPinPickerMap';
import AddressSearch from './AddressSearch';
import { BilingualField, Checkbox, SaveBar, SectionTitle, useFormDraft } from './formFields';
import type { TabProps } from './ListingEditor';

interface LocationDraft {
  addressHe: string;
  addressEn: string;
  lat: number | null;
  lng: number | null;
  showExactLocation: boolean;
  directionsNoteHe: string;
  directionsNoteEn: string;
}

function toDraft(loc: ListingLocation | undefined): LocationDraft {
  return {
    addressHe: loc?.addressHe ?? '',
    addressEn: loc?.addressEn ?? '',
    lat: loc && hasCoords(loc) ? loc.lat : null,
    lng: loc && hasCoords(loc) ? loc.lng : null,
    showExactLocation: loc?.showExactLocation !== false,
    directionsNoteHe: loc?.directionsNoteHe ?? '',
    directionsNoteEn: loc?.directionsNoteEn ?? '',
  };
}

export default function LocationTab({ listing }: TabProps) {
  const { t } = useTranslation();
  const { updateListing, saving } = useListingActions();
  const storedHidden = listing.location?.showExactLocation === false;
  const { draft, update, dirty, reset, save, justSaved, failed } = useFormDraft<LocationDraft>(
    toDraft(listing.location)
  );
  // Switching "show exact location" back on: the stored point is only approximate.
  const [needsExactPin, setNeedsExactPin] = useState(false);

  const point = draft.lat !== null && draft.lng !== null ? { lat: draft.lat, lng: draft.lng } : null;

  const toggleExact = (on: boolean) => {
    if (on && storedHidden) {
      // Decided by Yoel (2026-10-02): the exact spot was never stored, so place the pin again.
      update({ showExactLocation: true, lat: null, lng: null });
      setNeedsExactPin(true);
    } else {
      update({ showExactLocation: on });
      if (!on) setNeedsExactPin(false);
    }
  };

  const handleSave = async () => {
    const location = locationForSave({
      addressHe: draft.addressHe,
      addressEn: draft.addressEn,
      ...(point ?? {}),
      areaId: listing.location?.areaId || DEFAULT_AREA_ID,
      showExactLocation: draft.showExactLocation,
      directionsNoteHe: draft.directionsNoteHe,
      directionsNoteEn: draft.directionsNoteEn,
    });
    const ok = await save(() => updateListing(listing.id, { location }));
    if (ok) setNeedsExactPin(false);
  };

  return (
    <div className="space-y-5">
      <AddressSearch
        areaId={listing.location?.areaId}
        onPick={(p) => update({ addressHe: p.addressHe, addressEn: p.addressEn, lat: p.lat, lng: p.lng })}
      />
      <SectionTitle>{t('directory.admin.location.mapTitle')}</SectionTitle>
      <p className="text-sm text-ash">{t('directory.admin.location.mapHint')}</p>
      {needsExactPin && !point && (
        <p role="status" className="rounded-[10px] border border-ink/20 bg-fog px-3 py-2 text-sm text-ink">
          {t('directory.admin.location.placeAgain')}
        </p>
      )}
      <LazyPinPickerMap
        value={point}
        onChange={(p) => update({ lat: p.lat, lng: p.lng })}
        category={listing.category}
        approximate={!draft.showExactLocation}
        areaId={listing.location?.areaId}
      />
      {point ? (
        <button
          type="button"
          onClick={() => update({ lat: null, lng: null })}
          className="inline-flex items-center gap-2 min-h-[44px] text-sm text-ink hover:underline"
        >
          <MapPin className="w-4 h-4" aria-hidden />
          {t('directory.admin.location.removePin')}
        </button>
      ) : (
        <p className="text-sm text-ash">{t('directory.admin.location.noPin')}</p>
      )}

      <BilingualField
        labelHe={t('directory.field.addressHe')}
        labelEn={t('directory.field.addressEn')}
        valueHe={draft.addressHe}
        valueEn={draft.addressEn}
        onChangeHe={(v) => update({ addressHe: v })}
        onChangeEn={(v) => update({ addressEn: v })}
        maxLength={LISTING_LIMITS.addressLength}
        placeholderHe={t('directory.admin.location.addressPlaceholderHe')}
        placeholderEn={t('directory.admin.location.addressPlaceholderEn')}
      />

      <Checkbox
        label={t('directory.field.showExactLocation')}
        checked={draft.showExactLocation}
        onChange={toggleExact}
        hint={
          draft.showExactLocation
            ? t('directory.admin.location.exactOnHint')
            : t('directory.admin.location.exactOffHint')
        }
      />

      <BilingualField
        labelHe={t('directory.field.directionsNoteHe')}
        labelEn={t('directory.field.directionsNoteEn')}
        valueHe={draft.directionsNoteHe}
        valueEn={draft.directionsNoteEn}
        onChangeHe={(v) => update({ directionsNoteHe: v })}
        onChangeEn={(v) => update({ directionsNoteEn: v })}
        maxLength={LISTING_LIMITS.noteLength}
        placeholderHe={t('directory.admin.location.directionsPlaceholderHe')}
        placeholderEn={t('directory.admin.location.directionsPlaceholderEn')}
      />

      <SaveBar
        dirty={dirty}
        saving={saving}
        onSave={handleSave}
        onCancel={() => {
          setNeedsExactPin(false);
          reset();
        }}
        justSaved={justSaved}
        failed={failed}
      />
    </div>
  );
}
